import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBasket } from "@/context/BasketContext";
import { toast } from "sonner";
import { Clock, Ticket, Trophy, Check, X, Minus, Plus, Lock, ShoppingBag } from "lucide-react";
import DOMPurify from "dompurify";

function formatGBP(n, fraction = 2) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: fraction }).format(n);
}

export default function CompetitionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addItem } = useBasket();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerStatus, setAnswerStatus] = useState(null); // null | 'correct' | 'wrong'
  const [answerToken, setAnswerToken] = useState(null);
  const [tickets, setTickets] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/competitions/${id}`).then((r) => setC(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;
    if (!user) {
      toast.error("Please login to enter a raffle.");
      navigate(`/login?next=/competitions/${id}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/competitions/answer", {
        competition_id: id,
        answer_index: selectedAnswer,
      });
      if (data.correct) {
        setAnswerStatus("correct");
        setAnswerToken(data.answer_token);
        toast.success("Correct! Pick your tickets.");
      } else {
        setAnswerStatus("wrong");
        toast.error("Incorrect answer. Try again.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const addToBasket = () => {
    if (!canCheckout) return;
    addItem({
      competition_id: id,
      title: c.title,
      image_url: c.image_url,
      price_per_ticket: c.price_per_ticket,
      tickets,
      max_tickets: maxTickets,
      answer_token: answerToken,
    });
    toast.success(`Added ${tickets} ticket${tickets > 1 ? "s" : ""} to your basket.`, {
      action: { label: "View basket", onClick: () => navigate("/basket") },
    });
  };

  if (loading) return <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 text-white/50">Loading…</div>;
  if (!c) return <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 text-white/50">Competition not found. <Link to="/competitions" className="text-[color:var(--ufr-green)]">Back to raffles</Link></div>;

  const remaining = c.total_tickets - c.tickets_sold;
  const pct = Math.min(100, (c.tickets_sold / c.total_tickets) * 100);
  const total = (c.price_per_ticket * tickets).toFixed(2);
  const canCheckout = answerStatus === "correct" && answerToken;
  const maxTickets = Math.max(1, Math.min(c.max_tickets_per_user || 50, remaining));
  const ended = c.status !== "live" || (c.ends_at && new Date(c.ends_at) <= new Date());

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-16" data-testid="competition-detail-page">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/5] max-w-lg mx-auto rounded-md overflow-hidden border border-[color:var(--ufr-green)]/30 shadow-green-soft" data-testid="comp-hero-image">
            {c.image_url && (
              <>
                <img src={c.image_url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-35" />
                <img src={c.image_url} alt={c.title} className="absolute inset-0 w-full h-full object-contain" />
              </>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute top-3 left-3 flex items-center gap-2">
              {ended ? (
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-bold border border-white/25 bg-black/60 text-white/70">Ended</span>
              ) : (
                <span className="ufr-chip"><span className="live-dot" /> Live</span>
              )}
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-bold border border-yellow-400/40 bg-yellow-400/5 text-yellow-300">
                {c.rarity}
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">{c.subtitle}</div>
            <h1 className="font-display text-3xl md:text-5xl text-white mt-2 leading-tight">{c.title}</h1>
            {c.description && (
              <div
                className="text-sm text-white/70 leading-relaxed mt-4 [&_ul]:list-disc [&_ul]:pl-5 [&_b]:text-white [&_strong]:text-white"
                data-testid="comp-description"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.description) }}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Stat icon={<Ticket size={14} />} label="Per ticket" value={`£${c.price_per_ticket.toFixed(2)}`} accent />
            <Stat icon={<Clock size={14} />} label="Tickets left" value={remaining.toLocaleString()} />
          </div>

          <div>
            <div className="progress-track rounded-sm"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
            <div className="text-xs text-white/60 mt-2">{c.tickets_sold.toLocaleString()} / {c.total_tickets.toLocaleString()} entries</div>
          </div>

          <div className="ufr-divider" />

          {ended ? (
            <div className="ufr-card rounded-md p-6 border-[color:var(--ufr-green)]/30" data-testid="raffle-ended-card">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={16} className="text-[color:var(--ufr-green)]" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Raffle ended</div>
              </div>
              {c.winner ? (
                <>
                  <div className="font-display text-3xl text-white" data-testid="winner-name">
                    {c.winner.user_name || "Winner drawn"}
                  </div>
                  {c.winner.ticket_number != null && (
                    <div className="text-sm text-white/60 mt-1" data-testid="winner-ticket">
                      Winning ticket #{c.winner.ticket_number}
                    </div>
                  )}
                  <p className="text-sm text-white/60 mt-4">
                    Drawn live on stream from {c.tickets_sold.toLocaleString()} entries. Congratulations!
                  </p>
                </>
              ) : (
                <p className="text-sm text-white/60">This raffle has closed. The winner will be drawn live on stream soon.</p>
              )}
              <Link to="/competitions" className="btn-ufr-primary px-6 py-3 text-sm inline-flex items-center gap-2 mt-6" data-testid="ended-browse-live-btn">
                Browse live raffles
              </Link>
            </div>
          ) : (
          <>
          {/* Skill question */}
          <div className="ufr-card rounded-md p-5" data-testid="trivia-card">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={14} className="text-[color:var(--ufr-green)]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Skill question</div>
            </div>
            <div className="font-display text-xl text-white leading-snug">{c.question}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {c.options.map((opt, i) => {
                const isSelected = selectedAnswer === i;
                const disabled = answerStatus === "correct";
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => setSelectedAnswer(i)}
                    data-testid={`trivia-option-${i}`}
                    className={`text-left px-4 py-3 border text-sm transition-all ${
                      isSelected
                        ? "border-[color:var(--ufr-green)] bg-[rgba(34,255,102,0.08)] text-white"
                        : "border-white/10 bg-black/40 text-white/80 hover:border-white/30"
                    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <span className="font-bold mr-2 text-[color:var(--ufr-green)]">{String.fromCharCode(65 + i)}.</span>
                    {opt}
                  </button>
                );
              })}
            </div>

            {answerStatus === "correct" && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[color:var(--ufr-green)] font-bold" data-testid="answer-correct">
                <Check size={16} /> Correct! Choose your tickets below.
              </div>
            )}
            {answerStatus === "wrong" && (
              <div className="mt-4 flex items-center gap-2 text-sm text-red-400 font-bold" data-testid="answer-wrong">
                <X size={16} /> Wrong answer. Pick another and try again.
              </div>
            )}

            {answerStatus !== "correct" && (
              <button
                onClick={submitAnswer}
                disabled={selectedAnswer === null || submitting}
                className="btn-ufr-primary w-full mt-4 py-3 text-sm"
                data-testid="submit-answer-btn"
              >
                {submitting ? "Checking…" : "Submit answer"}
              </button>
            )}
          </div>

          {/* Ticket selector + checkout */}
          <div className={`ufr-card rounded-md p-5 transition-opacity ${canCheckout ? "opacity-100" : "opacity-60 pointer-events-none"}`} data-testid="checkout-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Tickets</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/40" data-testid="max-tickets-hint">Max {maxTickets} per order</div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button className="btn-ufr-ghost w-10 h-10 flex items-center justify-center" onClick={() => setTickets((t) => Math.max(1, t - 1))} data-testid="ticket-decrement">
                  <Minus size={14} />
                </button>
                <div className="font-display text-3xl text-white w-12 text-center" data-testid="ticket-count">{tickets}</div>
                <button className="btn-ufr-ghost w-10 h-10 flex items-center justify-center" onClick={() => setTickets((t) => Math.min(maxTickets, t + 1))} data-testid="ticket-increment">
                  <Plus size={14} />
                </button>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">Total</div>
                <div className="font-display text-2xl text-[color:var(--ufr-green)] glow-text" data-testid="ticket-total">£{total}</div>
              </div>
            </div>
            <button
              className="btn-ufr-primary w-full py-3 text-sm flex items-center justify-center gap-2 mt-5"
              onClick={addToBasket}
              disabled={!canCheckout}
              data-testid="add-to-basket-btn"
            >
              <ShoppingBag size={15} /> Add to basket
            </button>
            <p className="text-[11px] text-white/40 mt-3">
              Secure card payments powered by Nochex. Skill required • 18+
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className={`border rounded-md p-3 ${accent ? "border-[color:var(--ufr-green)]/40 bg-[rgba(34,255,102,0.05)]" : "border-white/10 bg-black/40"}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">
        {icon} {label}
      </div>
      <div className={`font-display text-xl mt-1 ${accent ? "text-[color:var(--ufr-green)]" : "text-white"}`}>{value}</div>
    </div>
  );
}
